import type { UsageSummary } from "../../../../src/infra/provider-usage.ts";
import type { GatewayBrowserClient } from "../gateway.ts";
import type {
  SessionsUsageResult,
  CostUsageSummary,
  NetworkSpendResult,
  SessionUsageTimeSeries,
} from "../types.ts";
import type { SessionTraceResult } from "../views/llm-stats-trace.ts";
import type { SessionLogEntry } from "../views/llm-stats.ts";

export type LlmStatsState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  usageLoading: boolean;
  usageResult: SessionsUsageResult | null;
  usageCostSummary: CostUsageSummary | null;
  usageError: string | null;
  spendLoading: boolean;
  spendResult: NetworkSpendResult | null;
  spendError: string | null;
  providerLoading: boolean;
  providerUsage: UsageSummary | null;
  providerError: string | null;
  usageStartDate: string;
  usageEndDate: string;
  usageTimeSeries: SessionUsageTimeSeries | null;
  usageTimeSeriesLoading: boolean;
  usageSessionLogs: SessionLogEntry[] | null;
  usageSessionLogsLoading: boolean;
  sessionTrace: SessionTraceResult | null;
  sessionTraceLoading: boolean;
  sessionTraceError: string | null;
};

export async function loadLlmStats(
  state: LlmStatsState,
  overrides?: {
    startDate?: string;
    endDate?: string;
  },
) {
  if (!state.client) {
    // No gateway client at all — surface it. Returning silently here rendered
    // every tab blank with no indication that the data path was never invoked.
    state.usageError = "Gateway client unavailable";
    state.spendError = "Gateway client unavailable";
    state.providerError = "Gateway client unavailable";
    return;
  }
  if (!state.connected) {
    // Disconnected is NOT the same as "no data". Previously this early-returned
    // before any request, so a failed WS handshake looked exactly like an empty
    // dataset across Overview/Analytics/Fleet Spend/Trace.
    state.usageError = "Not connected to gateway — waiting for connection";
    state.spendError = state.usageError;
    state.providerError = state.usageError;
    return;
  }
  if (state.usageLoading && state.spendLoading) {
    return;
  }
  state.usageLoading = true;
  state.spendLoading = true;
  state.usageError = null;
  state.spendError = null;

  const startDate = overrides?.startDate ?? state.usageStartDate;
  const endDate = overrides?.endDate ?? state.usageEndDate;

  try {
    state.providerLoading = true;
    const [sessionsRes, costRes, networkRes, providerRes] = await Promise.allSettled([
      state.client.request("sessions.usage", {
        startDate,
        endDate,
        limit: 1000,
        includeContextWeight: true,
      }),
      state.client.request("usage.cost", { startDate, endDate }),
      state.client.request("usage.networkCost", { startDate, endDate }),
      state.client.request("usage.status", {}),
    ]);

    if (sessionsRes.status === "fulfilled" && sessionsRes.value) {
      state.usageResult = sessionsRes.value as SessionsUsageResult;
    } else if (sessionsRes.status === "rejected") {
      state.usageError = String(sessionsRes.reason);
    }

    if (costRes.status === "fulfilled" && costRes.value) {
      state.usageCostSummary = costRes.value as CostUsageSummary;
    }

    if (networkRes.status === "fulfilled" && networkRes.value) {
      state.spendResult = networkRes.value as NetworkSpendResult;
    } else if (networkRes.status === "rejected") {
      state.spendError = String(networkRes.reason);
    }

    if (providerRes.status === "fulfilled" && providerRes.value) {
      state.providerUsage = providerRes.value as UsageSummary;
    } else if (providerRes.status === "rejected") {
      state.providerError = String(providerRes.reason);
    }
  } catch (err) {
    state.usageError = String(err);
    state.spendError = String(err);
  } finally {
    state.usageLoading = false;
    state.spendLoading = false;
    state.providerLoading = false;
  }
}

export async function loadSessionTimeSeries(state: LlmStatsState, sessionKey: string) {
  if (!state.client || !state.connected) {
    state.usageError = "Not connected to gateway — cannot load time series";
    return;
  }
  state.usageTimeSeriesLoading = true;
  state.usageTimeSeries = null;
  try {
    const res = await state.client.request("sessions.usage.timeseries", { key: sessionKey });
    if (res) state.usageTimeSeries = res as SessionUsageTimeSeries;
  } catch {
    state.usageTimeSeries = null;
  } finally {
    state.usageTimeSeriesLoading = false;
  }
}

export async function loadSessionLogs(state: LlmStatsState, sessionKey: string) {
  if (!state.client || !state.connected) return;
  state.usageSessionLogsLoading = true;
  state.usageSessionLogs = null;
  try {
    const res = await state.client.request("sessions.usage.logs", { key: sessionKey, limit: 500 });
    if (res && Array.isArray((res as { logs: SessionLogEntry[] }).logs)) {
      state.usageSessionLogs = (res as { logs: SessionLogEntry[] }).logs;
    }
  } catch {
    state.usageSessionLogs = null;
  } finally {
    state.usageSessionLogsLoading = false;
  }
}

export async function loadSessionTrace(state: LlmStatsState, sessionKey: string) {
  if (!state.client || !state.connected) {
    state.sessionTraceError = "Not connected to gateway — cannot load trace";
    return;
  }
  state.sessionTraceLoading = true;
  state.sessionTrace = null;
  state.sessionTraceError = null;
  try {
    const res = await state.client.request("sessions.usage.spans", { key: sessionKey });
    if (res) {
      state.sessionTrace = res as SessionTraceResult;
    }
  } catch (err) {
    state.sessionTraceError = String(err);
  } finally {
    state.sessionTraceLoading = false;
  }
}
